import { c as _c } from "react/compiler-runtime";
import React, { useRef } from "react";
import { SelectItem } from "./index";
type DeselectableSelectItemProps = React.ComponentPropsWithoutRef<typeof SelectItem> & {
  /**
   * Called when this item is picked while it is already the chosen value. Pass it only
   * on the chosen item of a question that allows clearing; without it the item is a
   * plain `SelectItem`.
   */
  onDeselect?: () => void;
};

/**
 * A `SelectItem` that can be picked again to clear the dropdown.
 *
 * Radix only reports a change of value, so reselecting is caught on the item itself,
 * on the same events Radix selects on: pointer up for a mouse, click for touch and pen,
 * Enter for the keyboard. Space is left alone because it also feeds type-ahead search.
 * A pointer up that Radix cancelled — the release of the click that opened the list —
 * is not a pick. The pointer type lives in a ref, as Radix keeps its own, so a re-render
 * between pointer down and up can't reset it and drop the clear.
 */
const DeselectableSelectItem = React.forwardRef<React.ElementRef<typeof SelectItem>, DeselectableSelectItemProps>((t0, forwardedRef) => {
  const $ = _c(18);
  let onDeselect;
  let props;
  if ($[0] !== t0) {
    ({
      onDeselect,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onDeselect;
    $[2] = props;
  } else {
    onDeselect = $[1];
    props = $[2];
  }
  const pointerType = useRef("touch");
  if (!onDeselect) {
    let t1;
    if ($[3] !== forwardedRef || $[4] !== props) {
      t1 = <SelectItem ref={forwardedRef} {...props} />;
      $[3] = forwardedRef;
      $[4] = props;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => {
      pointerType.current = event.pointerType;
    };
    t2 = event_0 => {
      pointerType.current = event_0.pointerType;
    };
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  let t3;
  let t4;
  let t5;
  if ($[8] !== onDeselect) {
    t3 = event_1 => {
      if (!event_1.defaultPrevented && pointerType.current === "mouse") {
        onDeselect();
      }
    };
    t4 = () => {
      if (pointerType.current !== "mouse") {
        onDeselect();
      }
    };
    t5 = event_2 => {
      if (event_2.key === "Enter") {
        onDeselect();
      }
    };
    $[8] = onDeselect;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[12] !== forwardedRef || $[13] !== props || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = <SelectItem ref={forwardedRef} {...props} onPointerDown={t1} onPointerMove={t2} onPointerUp={t3} onClick={t4} onKeyDown={t5} />;
    $[12] = forwardedRef;
    $[13] = props;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
});
DeselectableSelectItem.displayName = "DeselectableSelectItem";
export default DeselectableSelectItem;